import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { ProfileStateModel } from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { BehaviorSubject, combineLatest, filter, Observable, Subject, take, takeUntil } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { GetSavingAccount } from '../../actions/savings-account.action';
import { GetProfile } from '../../actions/user-profile.action';
import { ProjectSettingsState } from '../../state/project-settings.state';
import { RoleState, RoleStateModel } from '../../state/role.state';
import { ProfileState } from '../../state/user-profile.state';
import { handlePostLoginRetailUserRouting } from '../../utils/auth-routing.utils';
import { ConfigService } from '../config-service/config-service.service';
import { DeviceTypeService } from '../device-type/device-type.service';

@Injectable({
  providedIn: 'root',
})
export class SocketService {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  public message$: BehaviorSubject<string> = new BehaviorSubject('');
  public socket: Socket;
  private baseUrl = '';
  public token = '';
  private isUserSubscribed = false;
  roles!: Observable<RoleStateModel>;
  roleInfo: RoleStateModel;
  profileStateData!: Observable<ProfileStateModel>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  userDetail: any;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  constructor(
    public configService: ConfigService,
    private store: Store,
    private router: Router,
    private deviceTypeService: DeviceTypeService,
  ) {
    if (!this.configService.getActiveConfigType().isWorkflow) {
      this.configService
        .getApi('account_service')
        .pipe(take(1))
        .subscribe((api) => {
          this.baseUrl = api;
          this.setupSocketConnection();
        });
    }
  }

  setupSocketConnection() {
    this.token = sessionStorage.getItem('token') as string;
    if (!this.token) {
      console.log('No token found, skipping socket connection');
      return;
    }
    let path = '';
    if (this.baseUrl.includes('localhost')) {
      path = '/realtime/events';
    } else {
      path = '/api/account/realtime/events';
    }
    // Connect with the origin only, not the full baseUrl (which has a path in deployed
    // environments, e.g. /api/account) - socket.io-client treats a URL's pathname as the
    // namespace to join, so passing the full baseUrl put the client on a dynamically-created
    // non-default namespace where room-scoped broadcasts (orgEvent/userEvent/userOrgEvent)
    // never actually arrived. `path` above still carries the /api/account prefix so nginx's
    // ingress rule matches and routes the handshake to account-service in the first place.
    this.socket = io(new URL(this.baseUrl).origin, {
      path: path,
      transports: ['websocket'],
      extraHeaders: {
        Authorization: 'Bearer ' + this.token,
      },
      auth: {
        token: this.token,
      },
    });

    // Add connection event listeners for debugging
    this.socket.on('connect', () => {
      console.log('Socket connected:', this.socket.id);
      this.subscribeUser();
    });

    this.socket.on('disconnect', () => {
      console.log('Socket disconnected');
      this.isUserSubscribed = false;
    });

    this.socket.on('error', (error: any) => {
      console.error('Socket error:', error);
    });

    // Lazy load state selections to avoid circular dependency
    this.roles = this.store.select(RoleState.getRole);
    this.profileStateData = this.store.select(ProfileState.getProfile);

    // Wait for both profile and roles to be available, then subscribe once
    combineLatest([this.profileStateData, this.roles])
      .pipe(
        filter(([profile, roles]) => !!profile?.id && !!roles),
        take(1), // Only take the first emission when both are ready
        takeUntil(this.destroy$),
      )
      .subscribe(([profile, roles]) => {
        this.userDetail = profile;
        this.roleInfo = roles;
        console.log('[SocketService] User details and roles loaded, calling subscribeUser');
        this.subscribeUser();
      });
    this.listenToOrgEvents();
    this.listenToUserEvents();
    this.listenToUserOrgEvents();
  }
  // emit event
  inboundPayment() {
    this.socket.emit('inboundPayment');
  }

  joinRoom(room: string) {
    //console.log('joinRoom', room);
    this.socket.emit('joinRoom', room);
  }

  subscribeUser() {
    if (this.isUserSubscribed) {
      console.log('[SocketService] User already subscribed, skipping');
      return;
    }
    // Check if socket is connected
    if (!this.socket || !this.socket.connected) {
      console.log('[SocketService] Socket not connected yet, will subscribe on connect event');
      return;
    }
    if (this.token && this.userDetail?.id) {
      const obj = {
        userId: this.userDetail.id,
        device: this.roleInfo?.device ?? '',
        orgId: this.userDetail.activeOrganisationId,
        email: this.userDetail.email,
        firstName: this.userDetail.firstName,
        lastName: this.userDetail.lastName,
        phoneNumber: this.userDetail.phoneNumber ?? '',
      };
      this.socket.emit('subscribeUser', obj);
      this.isUserSubscribed = true;
      console.log('[SocketService] subscribe user successfully');
    } else {
      console.log('[SocketService] Cannot subscribe user - missing token or userDetail:', {
        hasToken: !!this.token,
        hasUserDetail: !!this.userDetail?.id,
      });
    }
  }

  /**
   * Switch organization rooms when user changes their active organization
   * This leaves the old org and user-org rooms and joins the new ones
   */
  switchOrganization(newOrgId: string) {
    if (!this.socket || !this.socket.connected) {
      console.log('Socket not connected, cannot switch organization');
      return;
    }

    if (!this.userDetail?.id) {
      console.log('User details not available, cannot switch organization');
      return;
    }

    const userId = this.userDetail.id;
    const oldOrgId = this.userDetail.activeOrganisationId;

    console.log(`Switching organization from ${oldOrgId} to ${newOrgId}`);

    // Emit switchOrganization event to backend
    this.socket.emit('switchOrganization', {
      userId,
      oldOrgId,
      newOrgId,
    });

    // Update local user detail
    this.userDetail.activeOrganisationId = newOrgId;
  }

  onJoinRoom() {
    console.log('joinedRoom');
  }

  /**
   * Disconnect socket and clean up event listeners
   * Call this method on user logout
   */
  disconnect(): void {
    if (this.socket) {
      console.log('Disconnecting socket and cleaning up event listeners');

      // Remove all event listeners
      this.socket.off('orgEvent');
      this.socket.off('userEvent');
      this.socket.off('userOrgEvent');
      this.socket.off('connect');
      this.socket.off('disconnect');
      this.socket.off('error');
      this.socket.off('connect_error');
      this.socket.off('reconnect_attempt');

      // Close the socket connection and prevent reconnection
      this.socket.disconnect();

      // Remove all listeners to prevent memory leaks
      this.socket.removeAllListeners();

      console.log('Socket disconnected successfully');
    }
  }

  /**
   * Event handler registry using Strategy Pattern
   * Maps event types to their handler functions
   */
  private eventHandlers: Map<string, (payload: any) => void> = new Map([
    ['account.created', (payload) => this.handleAccountCreated(payload)],
    ['customer.info.updated', (payload) => this.handleCustomerInfoUpdated(payload)],
    ['customer.document.uploaded', (payload) => this.handleCustomerDocumentUploadEvent(payload)],
    ['user.kyc.status.changed', (payload) => this.handleKycStatusChanged(payload)],
    ['function.custom', (payload) => this.handleFunctionCustom(payload)],
  ]);

  /**
   * Fan-out for function-emitted realtime events. `function.custom` is one
   * eventType carrying the function's own event name inside the payload, so a
   * single Map entry above dispatches to any number of listeners keyed by that
   * inner name — the eventHandlers Map itself allows one handler per type.
   */
  private functionEventListeners = new Map<string, Set<(data: Record<string, unknown>) => void>>();

  private handleFunctionCustom(payload: { name?: string; data?: Record<string, unknown> }): void {
    if (!payload?.name) return;
    this.functionEventListeners.get(payload.name)?.forEach((listener) => listener(payload.data ?? {}));
  }

  /**
   * Listen for one function-emitted event by its inner name (the `name` a
   * backend function passed to `realtime.emit`). Returns the unsubscribe.
   * Org- and user-scoped emissions both arrive here — the rooms differ, the
   * eventType does not.
   */
  onFunctionEvent(name: string, listener: (data: Record<string, unknown>) => void): () => void {
    let listeners = this.functionEventListeners.get(name);
    if (!listeners) {
      listeners = new Set();
      this.functionEventListeners.set(name, listeners);
    }
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0) this.functionEventListeners.delete(name);
    };
  }

  /**
   * Generic event listener that delegates to registered handlers
   */
  private handleEvent(eventChannel: string, event: any): void {
    console.log(`[${eventChannel}] Event received:`, event.eventType, event.payload);

    const handler = this.eventHandlers.get(event.eventType);
    if (handler) {
      handler(event.payload);
    } else {
      console.warn(`[${eventChannel}] No handler registered for event type: ${event.eventType}`);
    }
  }

  /**
   * Event Handlers - Strategy implementations
   */
  private handleAccountCreated(payload: {
    accountId: string;
    type: 'current' | 'savings' | 'nominated' | 'multi-currency';
  }): void {
    console.log('Account created:', payload.accountId);
    if (payload.type === 'savings') {
      this.store.dispatch(new GetSavingAccount());
    }
  }

  private handleCustomerInfoUpdated(payload: any): void {
    console.log('Customer info updated:', payload);
  }

  private handleCustomerDocumentUploadEvent(payload: any): void {
    console.log('Customer document uploaded:', payload);
    this.store.dispatch(new GetProfile());
  }

  private handleKycStatusChanged(payload: any): void {
    console.log('KYC status changed:', payload);
    this.store.dispatch(new GetProfile());
    const kycStatusRoutingConfig = this.store.selectSnapshot(ProjectSettingsState.getProjectSettings)
      ?.projectConfig?.kycStatusRoutingConfig;
    if (kycStatusRoutingConfig && kycStatusRoutingConfig?.retail) {
      const handled = handlePostLoginRetailUserRouting(
        kycStatusRoutingConfig,
        this.router,
        payload.newStatus,
        this.deviceTypeService,
      );
      if (handled) return;
    }
  }

  /**
   * Listen for organisation-level events
   * These events are broadcast to all users in the organisation
   */
  listenToOrgEvents(): void {
    console.log('Listening to organisation-level events');
    this.socket.on('orgEvent', (event: any) => this.handleEvent('orgEvent', event));
  }

  /**
   * Listen for user-level events
   * These events are specific to the current user across all their sessions
   */
  listenToUserEvents(): void {
    this.socket.on('userEvent', (event: any) => this.handleEvent('userEvent', event));
  }

  /**
   * Listen for user+organisation events
   * These events are specific to the current user in the current organisation context
   */
  listenToUserOrgEvents(): void {
    this.socket.on('userOrgEvent', (event: any) => this.handleEvent('userOrgEvent', event));
  }

  /**
   * Register a custom event handler
   * Allows dynamic registration of handlers at runtime
   */
  registerEventHandler(eventType: string, handler: (payload: any) => void): void {
    this.eventHandlers.set(eventType, handler);
    console.log(`Registered handler for event type: ${eventType}`);
  }

  /**
   * Unregister an event handler
   */
  unregisterEventHandler(eventType: string): void {
    this.eventHandlers.delete(eventType);
    console.log(`Unregistered handler for event type: ${eventType}`);
  }
}
